/* eslint-disable @typescript-eslint/no-this-alias, @typescript-eslint/no-explicit-any */
import L from "leaflet";

// owner
import { forceMapFilters } from "@cleanup/components/AdvancedFilters/FilterConstants";
import { ICON_TYPE, svgIcon } from "@/components/Leaflet/Map/Marker/SvgIcon";
import { PruneCluster } from "@/components/Leaflet/Map/PruneCluster/PruneCluster";
import { createAssetPruneCluster } from "@/components/Leaflet/Map/PruneCluster/assetCluster";
import GlobalUtils from "@/utils/GlobalUtils";

import searchUIClient from "@/apis/searchUIClient";
import DeviceIcon from "@images/device/Device-icon.svg";
import configurationUtils from "@/utils/IconConfiguration";

// Self-fetching asset cluster layer: the toggleable "asset devices" overlay on
// the shared CommonMapReact (geozone/infra maps, etc.). It fetches its own
// devices; all clustering/icon/marker behavior comes from the shared core
// (createAssetPruneCluster), so it stays identical to the page-driven sibling
// (AssetClusterLayer). Wired into the layer control via disableGroup().

interface DeviceData {
  id: number;
  name: string;
  currentLat: number;
  currentLng: number;
  lastSeenAlive?: string;
  lastNetworkLocationRadius?: number;
  decoratorColor?: string;
  location?: string;
  lastBeaconName?: string;
  lastActivity?: string;
  lastLocationSource?: string;
  trackerIcon?: string;
  coordinate?: {
    lat: number;
    lon: number;
    lastSeen?: string;
  };
}

interface AssetLayerOptions {
  ignoreTrackerId?: number;
}

interface SearchQuery {
  searchQueryType: string;
  queryObjectType: string;
  query: Array<{
    categoryId: string;
    filterKey: string;
    filterType: string;
    searchType: string;
    notFilter: boolean;
    filterValue: {
      includeEmpty: boolean;
      includeNoEmpty: boolean;
      selectedValues: unknown[];
    };
  }>;
  requestedTableColumns: Array<{
    columnKey: string;
    order: null;
  }>;
  selectedOrgId?: number;
}

const SelfFetchingAssetClusterLayerClass = (L.FeatureGroup as any).extend({
  options: {},
  bounds: L.latLngBounds([]),
  pruneCluster: null as any,
  assetCluster: null as any,

  disableGroup: function (isDisabled: boolean) {
    const self = this;
    if (self.pruneCluster && self.assetCluster) {
      self.assetCluster.setClusteringEnabled(!isDisabled);
      self.pruneCluster.ProcessView();
    }
  },

  updateData: function (orgId: number | null, options?: AssetLayerOptions) {
    const self = this;
    // Everything about how markers/clusters look and behave lives in the shared
    // core; this layer only differs in that it fetches its own devices.
    const assetCluster = createAssetPruneCluster({
      // Draw markers + clusters in whatever pane the composition assigned to this
      // layer (self.options.pane) so the page controls the z-order.
      pane: self.options?.pane,
    });

    self.assetCluster = assetCluster;
    self.pruneCluster = assetCluster.pruneCluster;

    const basic: SearchQuery = {
      searchQueryType: "BASIC",
      queryObjectType: "SIGFOX_DEVICE",
      query: [],
      requestedTableColumns: [
        {
          columnKey: "lastNetworkLocationRadius",
          order: null,
        },
        {
          columnKey: "currentLat",
          order: null,
        },
        {
          columnKey: "currentLng",
          order: null,
        },
        {
          columnKey: "name",
          order: null,
        },
        {
          columnKey: "decoratorColor",
          order: null,
        },
        {
          columnKey: "location",
          order: null,
        },
        {
          columnKey: "lastBeaconName",
          order: null,
        },
        {
          columnKey: "lastActivity",
          order: null,
        },
        // Status feeds the cluster popup's Status breakdown — without it every
        // device lands in the meaningless "OTHER" fallback bucket.
        {
          columnKey: "status",
          order: null,
        },
      ],
    };

    // Prefetch asset type + device tags so the marker hover popup can show them
    // here too (same columns the Asset map requests). Only for NORMAL orgs —
    // system/partner logins omit these columns, matching the Asset map strategy.
    if (window.loginInfo?.organisation?.organisationType === "NORMAL") {
      basic.requestedTableColumns.push(
        { columnKey: "assetType", order: null },
        { columnKey: "deviceTag", order: null }
      );
    }

    if (orgId) {
      basic.selectedOrgId = orgId;
    }

    forceMapFilters(basic as any, false);

    self.bound = L.latLngBounds([]);
    self.clearLayers();

    self.addLayer(self.pruneCluster);

    void searchUIClient.search(basic as any).then(({ data }: any) => {
      self.processData(data.data, options);
    });
  },

  transformData: function (beacon: DeviceData) {
    beacon.coordinate = {
      lat: beacon.currentLat,
      lon: beacon.currentLng,
      lastSeen: beacon.lastSeenAlive,
    };
  },

  isValidCoordinate: function (
    coordinate: { lat: number; lon: number } | undefined
  ): boolean {
    return !!(coordinate && (coordinate.lat || coordinate.lon));
  },

  processData: function (data: DeviceData[], options?: AssetLayerOptions) {
    const self = this;
    const lastMarkers: DeviceData[] = [];
    data.forEach((obj: DeviceData) => {
      if (options && options.ignoreTrackerId === obj.id) return;
      self.transformData(obj);
      const coordinate = obj.coordinate;
      const isValid = self.isValidCoordinate(coordinate);
      if (isValid) {
        lastMarkers.push(obj);
      }
    });
    self.processMarkers(lastMarkers);
  },

  processMarkers: function (lastMarkers: DeviceData[]) {
    const self = this;
    const markersById = new Map(lastMarkers.map((m) => [m.id, m]));

    // register the remaining markers
    markersById.forEach((device) => {
      // new marker, add to cluster
      const _coordinate = device.coordinate;
      if (!_coordinate) return;
      const activityState = configurationUtils.getActivityState(device as any);
      const state = configurationUtils.getState(device as any);
      const highestSeverity = configurationUtils.getHighestAlertSeverity(device as any);

      const icon = svgIcon({
        iconUrl: device.trackerIcon
          ? GlobalUtils.getFile(
              "device",
              device.id,
              "icon",
              device.trackerIcon,
              "small"
            )
          : DeviceIcon,
        id: String(device.id),
        trackerName: device.name,
        activityState: activityState,
        state: state,
        network: device?.lastLocationSource === "network",
        alertSeverity: highestSeverity,
        decoratorColor: device?.decoratorColor,
        type: ICON_TYPE.assetType,
      });

      const marker = new PruneCluster.Marker(
        _coordinate.lat || 0,
        _coordinate.lon || 0,
        {
          icon: icon,
          tooltip: device.name,
        }
      );
      marker.data.deviceInfo = device;
      (marker as any).getLatLng = function () {
        return new L.LatLng(this.position.lat, this.position.lon);
      };

      self.pruneCluster.RegisterMarker(marker);
    });

    self.pruneCluster.ProcessView();
  },

  getLayer: function (): any {
    return this;
  },

  getBounds: function (): L.LatLngBounds {
    return this.bounds;
  },
});

(L as any).AssetLayer = SelfFetchingAssetClusterLayerClass;
(L as any).assetLayer = function (opts?: L.LayerOptions): any {
  return new SelfFetchingAssetClusterLayerClass(opts);
};

export const SelfFetchingAssetClusterLayer = (L as any).assetLayer;
